/**
 * Pure series transforms for the sensor-detail charts — DB/React-free so they are
 * trivially unit-testable.
 *
 * The daily series itself is computed server-side (prorated across supply
 * intervals, gap-filled to the full window); these helpers only reshape it for the
 * chart and derive the smoothed trend and summary stats.
 */
import type { DailyConsumptionPoint } from "@/features/sensors/api";

export interface DailyPoint {
  /** ISO day (YYYY-MM-DD). */
  day: string;
  /** Short axis label (MM-DD). */
  label: string;
  consumption: number;
  /** Reporting completeness for the day, 0-100. */
  quality: number;
  /** Smoothed consumption trend (filled by {@link withTrend}). */
  trend?: number;
}

const round3 = (n: number) => Math.round(n * 1000) / 1000;

/**
 * Reshape the API's daily series for the chart, deriving per-day completeness
 * from the reported reading count against the device's nominal cadence.
 */
export function dailyToChartPoints(
  daily: DailyConsumptionPoint[],
  expectedPerDay: number,
): DailyPoint[] {
  const expected = Math.max(1, expectedPerDay);
  return daily.map((d) => ({
    day: d.day,
    label: d.day.slice(5),
    consumption: round3(d.consumption_m3),
    quality: Math.min(100, Math.round((d.reading_count / expected) * 100)),
  }));
}

/** Centered moving-average smoother — a readable stand-in for LOWESS. */
export function movingAverageTrend(values: number[], frac = 0.3): number[] {
  const n = values.length;
  if (n === 0) return [];
  const half = Math.max(1, Math.round((n * frac) / 2));
  return values.map((_, i) => {
    let sum = 0;
    let cnt = 0;
    for (let j = Math.max(0, i - half); j <= Math.min(n - 1, i + half); j += 1) {
      sum += values[j];
      cnt += 1;
    }
    return round3(cnt ? sum / cnt : values[i]);
  });
}

/** Attach a `trend` field (smoothed consumption) to each daily point. */
export function withTrend(points: DailyPoint[], frac = 0.3): DailyPoint[] {
  const trend = movingAverageTrend(
    points.map((p) => p.consumption),
    frac,
  );
  return points.map((p, i) => ({ ...p, trend: trend[i] }));
}

export function mean(values: number[]): number {
  return values.length ? values.reduce((a, b) => a + b, 0) / values.length : 0;
}
